import React, { useEffect } from 'react'
import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormHelperText, Grid, InputLabel, Select, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useParams } from 'react-router-dom'
import { enqueueSnackbar } from '../../../../components/snackbar'
import { DEService } from '../../../../api/clinical-pathways/des.service'
import { IHandoverSchema } from '../../../../@types/handover'
import { LoadingButton } from '@mui/lab'
import { useNavigate } from 'react-router-dom'
import { IClinicalRecordSchema } from '../../../../@types/clinicalRecord.type'
import SignatureDialog from '../../../../components/signature/SignatureDialog'
import { ClinicalRecords } from '../../../../api/clinicalRecords.service'

interface FormValues {
  handoverDate: Date | null
  handoverTo: string
  handoverType: string
  handoverReason: string
  handoverNotes: string

  screeningOutcome: string
  nextScreeningDate: Date | null
  screeningLocation: string
  screeningNotes: string

  referred: boolean
  referralDate: Date | null
  referralTo: string
  referralUrgency: string
  referralReason: string
  referralNotes: string

  treatmentRequired: boolean
  treatmentType: string
  treatmentStartDate: Date | null
  treatmentProvider: string
  treatmentNotes: string

  followUpRequired: boolean
  followUpDate: Date | null
  followUpLocation: string
  followUpInstructions: string

  additionalNotes: string
}

const HANDOVER_TYPES = [
  'Routine Handover',
  'Urgent Handover',
  'Referral to Specialist',
  'Transfer of Care',
  'Discharge',
  'Other',
]

const HANDOVER_REASONS = [
  'Routine Screening Completion',
  'Requires Specialist Care',
  'Complex Case Management',
  'Patient Request',
  'Clinical Indication',
  'Other',
]

const SCREENING_OUTCOMES = [
  'Normal - No DR',
  'Mild NPDR',
  'Moderate NPDR',
  'Severe NPDR',
  'Proliferative DR',
  'Diabetic Macular Edema',
  'Ungradable Images',
  'Other',
]

const REFERRAL_URGENCIES = ['Routine', 'Urgent', 'Emergency']

const TREATMENT_TYPES = [
  'Laser Treatment',
  'Intravitreal Injections',
  'Surgical Intervention',
  'Medical Management',
  'Lifestyle Modification',
  'Other',
]

type Props = {
  clinicalRecordId?: number;
  desRecord?: any;
  onUpdate?: () => void;
  clinicalRecord?: IClinicalRecordSchema | any;
};

export default function HandoverOutcomeForm({ clinicalRecordId, desRecord, onUpdate, clinicalRecord }: Props) {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState<FormValues | null>(null);

  const hasClinicalData = !!(
    clinicalRecord?.diabeticEyeScreeningId ||
    clinicalRecord?.mecsCuesId ||
    clinicalRecord?.glaucomaReferralRefinementId
  );

  const performHandover = async (values: FormValues) => {
    try {
      const service = new DEService();

      const payload = {
        ...values,
        clinicalRecordId,
        patientId,
        retinopathyGrade: values.screeningOutcome,
        reviewTimeline: values.nextScreeningDate ? values.nextScreeningDate.toISOString() : undefined,
        clinicalImpression: values.screeningNotes,
      };

      if (desRecord?.id) {
        await service.handoverDES(patientId!, desRecord.id, payload);
        enqueueSnackbar('Handover completed successfully', { variant: 'success' });
      } else {
        const res = await service.createDES(patientId!, payload);
        await service.handoverDES(patientId!, res.data.id, payload);
        enqueueSnackbar('Handover saved successfully', { variant: 'success' });
      }
      onUpdate && onUpdate();
    } catch (error) {
      enqueueSnackbar('Failed to save Handover', { variant: 'error' });
    }
  };

  const handleSignOff = async (signatureData: string) => {
    if (!clinicalRecordId) return;
    setIsSigning(true);
    try {
      const service = new ClinicalRecords();
      await service.signOff(clinicalRecordId, signatureData);
      enqueueSnackbar("Clinical record signed off successfully", {
        variant: "success",
      });
      setIsSignatureDialogOpen(false);

      // PROCEED WITH HANDOVER AFTER SIGN-OFF
      if (pendingValues) {
        await performHandover(pendingValues);
        setPendingValues(null);
      } else {
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to sign off clinical record", {
        variant: "error",
      });
    } finally {
      setIsSigning(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      handoverDate: new Date(),
      handoverTo: '',
      handoverType: '',
      handoverReason: '',
      handoverNotes: '',

      screeningOutcome: '',
      nextScreeningDate: null,
      screeningLocation: '',
      screeningNotes: '',

      referred: false,
      referralDate: null,
      referralTo: '',
      referralUrgency: '',
      referralReason: '',
      referralNotes: '',

      treatmentRequired: false,
      treatmentType: '',
      treatmentStartDate: null,
      treatmentProvider: '',
      treatmentNotes: '',

      followUpRequired: false,
      followUpDate: null,
      followUpLocation: '',
      followUpInstructions: '',

      additionalNotes: '',
    },
    validationSchema: Yup.object({
      handoverTo: Yup.string().required('Required'),
      handoverType: Yup.string().required('Required'),
      handoverReason: Yup.string().required('Required'),
      screeningOutcome: Yup.string().required('Required'),
      nextScreeningDate: Yup.date().when('screeningOutcome', {
        is: (val: string) => val && val !== 'Ungradable Images',
        then: (schema) => schema.required('Next screening date is required'),
      }),
      referralTo: Yup.string().when('referred', {
        is: true,
        then: (schema) => schema.required('Referral to is required'),
      }),
      referralUrgency: Yup.string().when('referred', {
        is: true,
        then: (schema) => schema.required('Referral urgency is required'),
      }),
      treatmentType: Yup.string().when('treatmentRequired', {
        is: true,
        then: (schema) => schema.required('Treatment type is required'),
      }),
      followUpDate: Yup.date().when('followUpRequired', {
        is: true,
        then: (schema) => schema.required('Follow-up date is required'),
      }),
    }),
    onSubmit: async (values) => {
      if (hasClinicalData && !clinicalRecord?.signedAt) {
        setPendingValues(values);
        setIsSignatureDialogOpen(true);
      } else {
        await performHandover(values);
      }
    },
  });

  const handleHandoverClick = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const fieldLabels: any = {
        handoverTo: 'Handover To',
        handoverType: 'Handover Type',
        handoverReason: 'Handover Reason',
        screeningOutcome: 'Screening Outcome',
        nextScreeningDate: 'Next Screening Date',
        referralTo: 'Referral To',
        referralUrgency: 'Referral Urgency',
        treatmentType: 'Treatment Type',
        followUpDate: 'Follow-up Date',
      };

      const missingFields = Object.keys(errors)
        .map((field) => fieldLabels[field] || field)
        .join(', ');

      enqueueSnackbar(`Missing required fields: ${missingFields}`, {
        variant: 'error',
      });

      // Mark all fields as touched to show inline errors
      const touched: any = {};
      Object.keys(errors).forEach((field) => {
        touched[field] = true;
      });
      formik.setTouched(touched);
    } else {
      formik.submitForm();
    }
  };

  useEffect(() => {
    if (desRecord && desRecord.id) {
      formik.setValues({
        handoverDate: desRecord.handoverDate ? new Date(desRecord.handoverDate) : new Date(),
        handoverTo: desRecord.handoverTo || '',
        handoverType: desRecord.handoverType || '',
        handoverReason: desRecord.handoverReason || '',
        handoverNotes: desRecord.handoverNotes || '',

        screeningOutcome: desRecord.retinopathyGrade || '',
        nextScreeningDate: desRecord.reviewTimeline ? new Date(desRecord.reviewTimeline) : null,
        screeningLocation: '', // Not in entity?
        screeningNotes: desRecord.clinicalImpression || '',

        referred: !!desRecord.referralRequired,
        referralDate: desRecord.referralDate ? new Date(desRecord.referralDate) : null, // Not in entity? I added referralNotes etc.
        // Wait, did I add referralDate?
        // Let's check entity update.
        referralTo: desRecord.referralTo || '', // Did I add referralTo?
        referralUrgency: desRecord.referralUrgency || '',
        referralReason: desRecord.referralReason || '',
        referralNotes: desRecord.referralNotes || '',

        treatmentRequired: !!desRecord.treatmentRequired,
        treatmentType: desRecord.treatmentType || '',
        treatmentStartDate: desRecord.treatmentStartDate ? new Date(desRecord.treatmentStartDate) : null,
        treatmentProvider: desRecord.treatmentProvider || '',
        treatmentNotes: desRecord.treatmentNotes || '',

        followUpRequired: !!desRecord.followUpRequired,
        followUpDate: desRecord.followUpDate ? new Date(desRecord.followUpDate) : null,
        followUpLocation: desRecord.followUpLocation || '',
        followUpInstructions: desRecord.followUpInstructions || '',

        additionalNotes: desRecord.otherFindings || '',
      } as any);
    }
  }, [desRecord]);

  const renderHandoverDetails = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Handover Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Handover Date"
              value={formik.values.handoverDate}
              onChange={(date) => formik.setFieldValue('handoverDate', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: formik.touched.handoverDate && Boolean(formik.errors.handoverDate),
                  helperText: formik.touched.handoverDate && (formik.errors.handoverDate as any),
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Handover To"
            name="handoverTo"
            value={formik.values.handoverTo}
            onChange={formik.handleChange}
            error={formik.touched.handoverTo && Boolean(formik.errors.handoverTo)}
            helperText={formik.touched.handoverTo && (formik.errors.handoverTo as any)}
            margin="normal"
            placeholder="e.g., GP, Retina Specialist"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth margin="normal" error={formik.touched.handoverType && Boolean(formik.errors.handoverType)}>
            <InputLabel>Handover Type</InputLabel>
            <Select name="handoverType" value={formik.values.handoverType} onChange={formik.handleChange} label="Handover Type">
              {HANDOVER_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.handoverType && formik.errors.handoverType && (
              <FormHelperText>{formik.errors.handoverType as any}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="normal" error={formik.touched.handoverReason && Boolean(formik.errors.handoverReason)}>
            <InputLabel>Handover Reason</InputLabel>
            <Select name="handoverReason" value={formik.values.handoverReason} onChange={formik.handleChange} label="Handover Reason">
              {HANDOVER_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.handoverReason && formik.errors.handoverReason && (
              <FormHelperText>{formik.errors.handoverReason as any}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Handover Notes"
            name="handoverNotes"
            value={formik.values.handoverNotes}
            onChange={formik.handleChange}
            multiline
            rows={3}
            margin="normal"
          />
        </Grid>
      </Grid>
    </Box>
  )

  const renderScreeningOutcome = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Screening Outcome
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth margin="normal" error={formik.touched.screeningOutcome && Boolean(formik.errors.screeningOutcome)}>
            <InputLabel>Screening Outcome</InputLabel>
            <Select name="screeningOutcome" value={formik.values.screeningOutcome} onChange={formik.handleChange} label="Screening Outcome">
              {SCREENING_OUTCOMES.map((outcome) => (
                <MenuItem key={outcome} value={outcome}>
                  {outcome}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.screeningOutcome && formik.errors.screeningOutcome && (
              <FormHelperText>{formik.errors.screeningOutcome as any}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Next Screening Date"
              value={formik.values.nextScreeningDate}
              onChange={(date) => formik.setFieldValue('nextScreeningDate', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: formik.touched.nextScreeningDate && Boolean(formik.errors.nextScreeningDate),
                  helperText: formik.touched.nextScreeningDate && (formik.errors.nextScreeningDate as any),
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Screening Location" name="screeningLocation" value={formik.values.screeningLocation} onChange={formik.handleChange} margin="normal" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Screening Notes" name="screeningNotes" value={formik.values.screeningNotes} onChange={formik.handleChange} multiline rows={3} margin="normal" />
        </Grid>
      </Grid>
    </Box>
  )

  const renderReferralInformation = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Referral Information
      </Typography>
      <FormControlLabel
        control={<Checkbox name="referred" checked={formik.values.referred} onChange={(e) => {
          formik.setFieldValue('referred', e.target.checked)
          if (!e.target.checked) {
            formik.setFieldValue('referralDate', null)
            formik.setFieldValue('referralTo', '')
            formik.setFieldValue('referralUrgency', '')
            formik.setFieldValue('referralReason', '')
            formik.setFieldValue('referralNotes', '')
          }
        }} />}
        label="Patient Referred"
        sx={{ display: 'block', mb: 2 }}
      />
      {formik.values.referred && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Referral Date"
                value={formik.values.referralDate}
                onChange={(date) => formik.setFieldValue('referralDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    error: formik.touched.referralDate && Boolean(formik.errors.referralDate),
                    helperText: formik.touched.referralDate && (formik.errors.referralDate as any),
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Referred To" name="referralTo" value={formik.values.referralTo} onChange={formik.handleChange} error={formik.touched.referralTo && Boolean(formik.errors.referralTo)} helperText={formik.touched.referralTo && (formik.errors.referralTo as any)} margin="normal" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="normal" error={formik.touched.referralUrgency && Boolean(formik.errors.referralUrgency)}>
              <InputLabel>Referral Urgency</InputLabel>
              <Select name="referralUrgency" value={formik.values.referralUrgency} onChange={formik.handleChange} label="Referral Urgency">
                {REFERRAL_URGENCIES.map((urgency) => (
                  <MenuItem key={urgency} value={urgency}>
                    {urgency}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.referralUrgency && formik.errors.referralUrgency && (
                <FormHelperText>{formik.errors.referralUrgency as any}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Referral Reason" name="referralReason" value={formik.values.referralReason} onChange={formik.handleChange} multiline rows={2} margin="normal" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Referral Notes" name="referralNotes" value={formik.values.referralNotes} onChange={formik.handleChange} multiline rows={3} margin="normal" />
          </Grid>
        </Grid>
      )}
    </Box>
  )

  const renderTreatmentInformation = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Treatment Information
      </Typography>
      <FormControlLabel
        control={<Checkbox name="treatmentRequired" checked={formik.values.treatmentRequired} onChange={(e) => {
          formik.setFieldValue('treatmentRequired', e.target.checked)
          if (!e.target.checked) {
            formik.setFieldValue('treatmentType', '')
            formik.setFieldValue('treatmentStartDate', null)
            formik.setFieldValue('treatmentProvider', '')
            formik.setFieldValue('treatmentNotes', '')
          }
        }} />}
        label="Treatment Required"
        sx={{ display: 'block', mb: 2 }}
      />
      {formik.values.treatmentRequired && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="normal" error={formik.touched.treatmentType && Boolean(formik.errors.treatmentType)}>
              <InputLabel>Treatment Type</InputLabel>
              <Select name="treatmentType" value={formik.values.treatmentType} onChange={formik.handleChange} label="Treatment Type">
                {TREATMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.treatmentType && formik.errors.treatmentType && (
                <FormHelperText>{formik.errors.treatmentType as any}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Treatment Start Date"
                value={formik.values.treatmentStartDate}
                onChange={(date) => formik.setFieldValue('treatmentStartDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Treatment Provider" name="treatmentProvider" value={formik.values.treatmentProvider} onChange={formik.handleChange} margin="normal" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Treatment Notes" name="treatmentNotes" value={formik.values.treatmentNotes} onChange={formik.handleChange} multiline rows={3} margin="normal" />
          </Grid>
        </Grid>
      )}
    </Box>
  )

  const renderFollowUpPlan = () => (
    <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Follow-up Plan
      </Typography>
      <FormControlLabel
        control={<Checkbox name="followUpRequired" checked={formik.values.followUpRequired} onChange={(e) => {
          formik.setFieldValue('followUpRequired', e.target.checked)
          if (!e.target.checked) {
            formik.setFieldValue('followUpDate', null)
            formik.setFieldValue('followUpLocation', '')
            formik.setFieldValue('followUpInstructions', '')
          }
        }} />}
        label="Follow-up Required"
        sx={{ display: 'block', mb: 2 }}
      />
      {formik.values.followUpRequired && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Follow-up Date"
                value={formik.values.followUpDate}
                onChange={(date) => formik.setFieldValue('followUpDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Follow-up Location" name="followUpLocation" value={formik.values.followUpLocation} onChange={formik.handleChange} margin="normal" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Follow-up Instructions" name="followUpInstructions" value={formik.values.followUpInstructions} onChange={formik.handleChange} multiline rows={3} margin="normal" />
          </Grid>
        </Grid>
      )}
    </Box>
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {!hasClinicalData && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main" gutterBottom>
            No clinical data found.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please complete at least one clinical form before signing off.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <LoadingButton variant="contained" disabled>Sign Off Clinical Record</LoadingButton>
            <LoadingButton variant="outlined" disabled>Do Handover</LoadingButton>
          </Stack>
        </Box>
      )}

      {hasClinicalData && !clinicalRecord?.signedAt && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="info.main" gutterBottom>
            Clinical record must be signed off before handover.
          </Typography>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => setIsSignatureDialogOpen(true)}
          >
            Sign Off Clinical Record
          </LoadingButton>
        </Box>
      )}

      {hasClinicalData && clinicalRecord?.signedAt && (
        <Box
          sx={{
            mt: 3,
            mb: 2,
            mx: 3,
            p: 2,
            border: "1px solid #4caf50",
            borderRadius: 1,
            bgcolor: "#e8f5e9",
          }}
        >
          <Typography variant="subtitle2" color="success.dark">
            Signed Off By: {clinicalRecord.signedByUser?.username || "Practitioner"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Date: {clinicalRecord.signedAt ? new Date(clinicalRecord.signedAt).toLocaleDateString() : "N/A"}
          </Typography>
          <Typography variant="body2" color="success.main" sx={{ fontWeight: "bold" }}>
            Status: Signed
          </Typography>
        </Box>
      )}

      {hasClinicalData && clinicalRecord?.signedAt && (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Handover & Outcome
          </Typography>
          {renderHandoverDetails()}
          {renderScreeningOutcome()}
          {renderReferralInformation()}
          {renderTreatmentInformation()}
          {renderFollowUpPlan()}
          <Divider sx={{ my: 3 }} />
          <TextField fullWidth label="Additional Notes" name="additionalNotes" value={formik.values.additionalNotes} onChange={formik.handleChange} multiline rows={4} margin="normal" />
          <Divider sx={{ my: 3 }} />
          <Stack direction="row-reverse" mt="40px" gap="30px" mr="17px" mb="20px">
            <LoadingButton type="button" variant="contained" onClick={() => navigate(-1)}>
              Exit
            </LoadingButton>
            <LoadingButton
              type="button"
              variant="outlined"
              loading={formik.isSubmitting}
              onClick={handleHandoverClick}
            >
              Do Handover
            </LoadingButton>
          </Stack>
        </Box>
      )}
      <SignatureDialog
        open={isSignatureDialogOpen}
        onClose={() => setIsSignatureDialogOpen(false)}
        onConfirm={handleSignOff}
        loading={isSigning}
      />
    </LocalizationProvider>
  )
}
